// src/pages/admin/Vendors.jsx
import React, { useState, useEffect } from "react";
import { adminApi } from "../../api/adminApi";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Alert,
  Rating,
  Card,
  CardContent,
  Grid,
  TablePagination,
  Divider,
} from "@mui/material";
import {
  Search,
  MoreVert,
  CheckCircle,
  Block,
  Refresh,
  Store,
  Star,
  Verified,
  Edit,
  Percent,
  AccountBalance,
  AttachMoney,
} from "@mui/icons-material";

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [verifyDialog, setVerifyDialog] = useState(false);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);

  // ✅ NEW: Balance Dialog States
  const [openBalanceDialog, setOpenBalanceDialog] = useState(false);
  const [vendorFinance, setVendorFinance] = useState(null);
  const [loadingFinance, setLoadingFinance] = useState(false);

  const [editFormData, setEditFormData] = useState({
    storeName: "",
    businessEmail: "",
    businessPhone: "",
    commissionRate: 10,
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
  });

  const [newRating, setNewRating] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getVendors({
        page: page + 1,
        limit: rowsPerPage,
        status: filterStatus !== "all" ? filterStatus : undefined,
        search: searchTerm || undefined,
      });
      setVendors(response.data.vendors || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [page, rowsPerPage, filterStatus]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === "" || e.target.value.length > 2) {
      setPage(0);
      fetchVendors();
    }
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, vendor) => {
    setAnchorEl(event.currentTarget);
    setSelectedVendor(vendor);
    setNewRating(vendor.rating || 0);

    setEditFormData({
      storeName: vendor.storeName || "",
      businessEmail: vendor.businessEmail || "",
      businessPhone: vendor.businessPhone || "",
      commissionRate: vendor.commissionRate ?? 10,
      bankAccountName: vendor.bankAccountName || "",
      bankAccountNumber: vendor.bankAccountNumber || "",
      bankName: vendor.bankName || "",
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ✅ NEW: Handle View Balance
  const handleViewBalance = async () => {
    if (!selectedVendor) return;
    setLoadingFinance(true);
    setOpenBalanceDialog(true);
    handleMenuClose();
    try {
      const response = await adminApi.getVendorFinance(selectedVendor.id);
      setVendorFinance(response.data);
    } catch (err) {
      setError("Failed to load vendor finance details.");
    } finally {
      setLoadingFinance(false);
    }
  };

  const handleVerifyVendor = async () => {
    if (selectedVendor) {
      try {
        await adminApi.verifyVendor(selectedVendor.id);
        setSuccess(`✅ ${selectedVendor.storeName} verified successfully!`);
        fetchVendors();
        setVerifyDialog(false);
        setSelectedVendor(null);
      } catch (error) {
        setError(error.response?.data?.message || "Failed to verify vendor");
      }
    }
  };

  const handleSuspendVendor = async () => {
    if (selectedVendor) {
      try {
        await adminApi.suspendVendor(selectedVendor.id);
        setSuccess(`⛔ ${selectedVendor.storeName} suspended successfully!`);
        fetchVendors();
        setSuspendDialog(false);
        setSelectedVendor(null);
      } catch (error) {
        setError(error.response?.data?.message || "Failed to suspend vendor");
      }
    }
  };

  const handleActivateVendor = async () => {
    if (selectedVendor) {
      try {
        await adminApi.verifyVendor(selectedVendor.id);
        setSuccess(`✅ ${selectedVendor.storeName} activated successfully!`);
        fetchVendors();
        setSelectedVendor(null);
        handleMenuClose();
      } catch (error) {
        setError(error.response?.data?.message || "Failed to activate vendor");
      }
    }
  };

  const handleUpdateRating = async () => {
    if (selectedVendor) {
      try {
        await adminApi.updateVendorRating(selectedVendor.id, newRating);
        setSuccess(
          `⭐ ${selectedVendor.storeName} rating updated to ${newRating}`,
        );
        fetchVendors();
        setRatingDialog(false);
        setSelectedVendor(null);
      } catch (error) {
        setError(error.response?.data?.message || "Failed to update rating");
      }
    }
  };

  const handleEditVendor = async () => {
    if (selectedVendor) {
      try {
        if (
          editFormData.commissionRate < 0 ||
          editFormData.commissionRate > 100
        ) {
          setError("Commission rate must be between 0 and 100");
          return;
        }
        await adminApi.updateVendor(selectedVendor.id, editFormData);
        setSuccess(`✏️ ${editFormData.storeName} updated successfully!`);
        fetchVendors();
        setEditDialog(false);
        setSelectedVendor(null);
      } catch (error) {
        setError(error.response?.data?.message || "Failed to update vendor");
      }
    }
  };

  const getStatusColor = (vendor) => {
    if (vendor.isVerified && vendor.isActive) return "success";
    if (vendor.isVerified && !vendor.isActive) return "error";
    return "warning";
  };

  const getStatusLabel = (vendor) => {
    if (vendor.isVerified && vendor.isActive) return "Active";
    if (vendor.isVerified && !vendor.isActive) return "Suspended";
    return "Pending";
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading && vendors.length === 0 && page === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Vendors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage platform vendors, verify accounts, and update ratings
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchVendors}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Store color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {totalCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Verified color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {vendors.filter((v) => v.isVerified && v.isActive).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verified & Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Block color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {vendors.filter((v) => !v.isActive).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Suspended
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Star color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {vendors.length > 0
                  ? (
                      vendors.reduce((sum, v) => sum + (v.rating || 0), 0) /
                      vendors.length
                    ).toFixed(1)
                  : "0.0"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1}>
            <Chip
              label="All"
              onClick={() => handleFilterChange("all")}
              color={filterStatus === "all" ? "primary" : "default"}
              clickable
            />
            <Chip
              label="Active"
              onClick={() => handleFilterChange("active")}
              color={filterStatus === "active" ? "success" : "default"}
              clickable
            />
            <Chip
              label="Pending"
              onClick={() => handleFilterChange("pending")}
              color={filterStatus === "pending" ? "warning" : "default"}
              clickable
            />
            <Chip
              label="Suspended"
              onClick={() => handleFilterChange("suspended")}
              color={filterStatus === "suspended" ? "error" : "default"}
              clickable
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "grey.50" }}>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No vendors found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow key={vendor.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {getInitials(vendor.storeName)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {vendor.storeName || "Unknown Store"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vendor.User?.name || "Unknown Owner"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {vendor.businessEmail || vendor.User?.email || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {vendor.Products?.length || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Rating
                          value={vendor.rating || 0}
                          size="small"
                          readOnly
                        />
                        <Typography variant="caption">
                          ({vendor.rating || 0})
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<Percent />}
                        label={`${vendor.commissionRate ?? 10}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={vendor.isActive ? <CheckCircle /> : <Block />}
                        label={getStatusLabel(vendor)}
                        size="small"
                        color={getStatusColor(vendor)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, vendor)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setEditDialog(true);
            handleMenuClose();
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Vendor
        </MenuItem>
        {selectedVendor && !selectedVendor.isVerified && (
          <MenuItem
            onClick={() => {
              setVerifyDialog(true);
              handleMenuClose();
            }}
          >
            <Verified fontSize="small" sx={{ mr: 1 }} /> Verify Vendor
          </MenuItem>
        )}
        {selectedVendor && selectedVendor.isActive ? (
          <MenuItem
            onClick={() => {
              setSuspendDialog(true);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            <Block fontSize="small" sx={{ mr: 1 }} /> Suspend Vendor
          </MenuItem>
        ) : (
          selectedVendor && (
            <MenuItem
              onClick={() => {
                handleActivateVendor();
                handleMenuClose();
              }}
              sx={{ color: "success.main" }}
            >
              <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Activate Vendor
            </MenuItem>
          )
        )}
        {/* ✅ NEW: View Balance Menu Item */}
        <MenuItem
          onClick={() => {
            handleViewBalance();
            handleMenuClose();
          }}
        >
          <AttachMoney fontSize="small" sx={{ mr: 1 }} /> View Balance
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRatingDialog(true);
            handleMenuClose();
          }}
        >
          <Star fontSize="small" sx={{ mr: 1 }} /> Manage Rating
        </MenuItem>
      </Menu>

      {/* ✅ NEW: Balance Details Dialog */}
      <Dialog
        open={openBalanceDialog}
        onClose={() => setOpenBalanceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Vendor Financial Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {loadingFinance ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : vendorFinance ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Vendor
                </Typography>
                <Typography variant="h6">{vendorFinance.storeName}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Current Available Balance
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  ${vendorFinance.availableBalance?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    ${vendorFinance.totalRevenue?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Platform Commission ({vendorFinance.commissionAmount || 0}%)
                  </Typography>
                  <Typography variant="body1" color="error">
                    -${vendorFinance.commissionAmount?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Already Withdrawn
                  </Typography>
                  <Typography variant="body1">
                    ${vendorFinance.totalWithdrawn?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Pending Requests
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    ${vendorFinance.totalPending?.toFixed(2) || "0.00"}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No financial data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBalanceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Vendor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Store Name"
              value={editFormData.storeName}
              onChange={(e) =>
                setEditFormData({ ...editFormData, storeName: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Business Email"
              value={editFormData.businessEmail}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  businessEmail: e.target.value,
                })
              }
            />
            <TextField
              fullWidth
              label="Business Phone"
              value={editFormData.businessPhone}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  businessPhone: e.target.value,
                })
              }
            />
            <TextField
              fullWidth
              label="Commission Rate (%)"
              type="number"
              value={editFormData.commissionRate}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  commissionRate: parseFloat(e.target.value),
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Percent />
                  </InputAdornment>
                ),
                inputProps: { min: 0, max: 100, step: 0.5 },
              }}
              helperText="Override the default platform commission for this specific vendor."
            />

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="primary" fontWeight="bold">
              <AccountBalance sx={{ mr: 1, fontSize: 18 }} /> Vendor Bank
              Details
            </Typography>

            <TextField
              fullWidth
              label="Bank Account Name"
              value={editFormData.bankAccountName}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  bankAccountName: e.target.value,
                })
              }
            />
            <TextField
              fullWidth
              label="Bank Account Number"
              value={editFormData.bankAccountNumber}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  bankAccountNumber: e.target.value,
                })
              }
            />
            <TextField
              fullWidth
              label="Bank Name"
              value={editFormData.bankName}
              onChange={(e) =>
                setEditFormData({ ...editFormData, bankName: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditVendor}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifyDialog}
        onClose={() => setVerifyDialog(false)}
        maxWidth="xs"
      >
        <DialogTitle>Verify Vendor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to verify "{selectedVendor?.storeName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Cancel</Button>
          <Button
            onClick={handleVerifyVendor}
            color="success"
            variant="contained"
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={suspendDialog}
        onClose={() => setSuspendDialog(false)}
        maxWidth="xs"
      >
        <DialogTitle>Suspend Vendor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to suspend "{selectedVendor?.storeName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSuspendVendor}
            color="error"
            variant="contained"
          >
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ratingDialog}
        onClose={() => setRatingDialog(false)}
        maxWidth="xs"
      >
        <DialogTitle>Update Vendor Rating</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, alignItems: "center" }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: "primary.main" }}>
              {selectedVendor?.storeName?.[0]?.toUpperCase() || "V"}
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              {selectedVendor?.storeName}
            </Typography>
            <Rating
              value={newRating}
              onChange={(_, newValue) => setNewRating(newValue || 0)}
              size="large"
              precision={0.5}
            />
            <Typography variant="body2" color="text.secondary">
              Current Rating: {selectedVendor?.rating || 0}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateRating}
            variant="contained"
            color="primary"
          >
            Update Rating
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vendors;